import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch, API, getCsrfToken } from "../utility/apiFetch.js";

// Generic photo-gallery hooks shared by grows, inventory items, and recipes.
// `resource` is the URL segment ("grows" | "inventory" | "recipes") and `id` is that
// resource's id. Query key: ["photos", resource, id].
//
// Uploads use raw fetch + FormData (apiFetch JSON-stringifies and can't do multipart).
// We must NOT set Content-Type — the browser adds the multipart boundary. CSRF token is
// sent explicitly. Presigned thumbnail URLs live 24h server-side, so we refetch on focus
// (staleTime < TTL) to re-mint before they expire on a long-open page.

export function usePhotosQuery(resource, id) {
  return useQuery({
    queryKey: ["photos", resource, id],
    queryFn: () => apiFetch(`${resource}/${id}/photos`),
    enabled: !!id,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}

// postPhoto sends a multipart upload to the given method (POST create / PUT replace).
async function postPhoto(method, path, { file, caption, noteId, phaseId }) {
  const form = new FormData();
  form.append("photo", file);
  if (caption) form.append("caption", caption);
  if (noteId != null) form.append("note_id", String(noteId));
  if (phaseId != null) form.append("phase_id", String(phaseId));

  const res = await fetch(`${API}${path}`, {
    method,
    credentials: "include",
    headers: { "X-CSRF-Token": getCsrfToken() }, // no Content-Type — browser sets boundary
    body: form,
  });
  if (!res.ok) {
    let body = {};
    try { body = await res.json(); } catch { /* ignore */ }
    const err = new Error(body.error || `${path} ${res.status}`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return res.json();
}

export function useUploadPhoto(resource, id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args) => postPhoto("POST", `${resource}/${id}/photos`, args),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["photos", resource, id] }),
  });
}

export function useReplacePhoto(resource, id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ photoId, ...args }) => postPhoto("PUT", `${resource}/${id}/photos/${photoId}`, args),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["photos", resource, id] }),
  });
}

export function useDeletePhoto(resource, id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (photoId) => apiFetch(`${resource}/${id}/photos/${photoId}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["photos", resource, id] }),
  });
}

export function useSetCoverPhoto(resource, id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (photoId) => apiFetch(`${resource}/${id}/photos/${photoId}/cover`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["photos", resource, id] });
      // Grow cards show cover_photo_url from the grows list — keep it in sync.
      if (resource === "grows") qc.invalidateQueries({ queryKey: ["grows"] });
    },
  });
}

export function useRemoveCoverPhoto(resource, id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch(`${resource}/${id}/cover`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["photos", resource, id] });
      if (resource === "grows") qc.invalidateQueries({ queryKey: ["grows"] });
    },
  });
}
